import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { crewNotYetOpened, line, Color, DEFAULT_PAGE_SIZE, fill, loadOutFires, Size, formatDateTime, hoursLabel, searchWithin, serviceReportStateLabel, t, visitIsFixed, visitWindowEditable, wallClockAfterMinutes, wallClockInZone, WORK_ORDER_VISIT_STATUS_LABELS, zoneAbbreviation, type Chip, type ColumnDef, type DateFormat, type EditVisitInput, type PageSize, type SortState, type WorkOrderVisitPerson, type WorkOrderVisitRow, type WorkOrderVisitStatus, type WorkOrderVisitsAnswer, type BoardCandidate, type WhoCanGoAnswer, type VisitLineOption, type VisitLinesInput, visitLinesInput, viewerScopeKey, type VisitDeletion } from '@bms2/shared';
import { AlertBanner, Button, Checkbox, Chips, DatePicker, Pill, RadioGroup, Select, TextInput } from '../../components/controls';
import { ListViewer } from '../../components/composites/ListViewer';
import { FormFrame } from '../../components/forms';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { get, post, useApi } from './api';
import { WORK_ORDERS_PATH, useWorkOrderOptions } from './WorkOrderPanels';
import { useAddNew } from './AddNewHost';
import styles from './RecordPage.module.css';
import bookingStyles from './VisitBooking.module.css';
import { jobChecklistRoute } from './JobChecklistPage';
import { WorkOrderLoadOut } from './WorkOrderLoadOut';
import { refreshMenuCounts } from '../../shell/menuCounts';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const workOrderVisitsPath = (workOrderId: string, visitId?: string, action?: 'cancel' | 'complete' | 'send' | 'edit' | 'notes' | 'notes/photo' | 'deletion' | 'delete-anyway'): string =>
  `${WORK_ORDERS_PATH}/${workOrderId}/visits${visitId ? `/${visitId}` : ''}${action ? `/${action}` : ''}`;

/** 🔒 The Service Report's page read (decision 2910): the visit's day for the bar is the viewer's own zone. */
export const visitPagePath = (workOrderId: string, visitId: string, timeZone: string): string => `${workOrderVisitsPath(workOrderId, visitId)}?timeZone=${encodeURIComponent(timeZone)}`;

/** 🔒 The visit's own page, under its job (decision 1908; T1263). */
export const visitPageRoute = (workOrderId: string, visitId: string): string => `/work-orders/${workOrderId}/visits/${visitId}`;

/** What Book Another Visit opens on: blank from the tile, the visit's own crew and lead from Book a Return Visit. */
export interface Booking {
  startsAt: string;
  endsAt: string;
  workers: Chip[];
  lead: string;
  /** 🔒 Decision 3100: Fixed with its typed reason, or Can move where unset. */
  fixed?: boolean;
  fixedReason?: string;
  /** 🔒 Decision 5571: the lines the visit covers by id; absent covers every line. */
  lineIds?: string[];
  /** 🔒 Decision 5571: each worker's lines by employment; a worker absent is sent for every covered line. */
  workerLines?: Record<string, string[]>;
}
export const EMPTY_BOOKING: Booking = { startsAt: '', endsAt: '', workers: [], lead: '', fixed: false, fixedReason: '' };

/** 🔒 The visit Edit Visit opens on (decision 2813), read from a Visits tile row or from the visit's page. */
export interface VisitToEdit {
  id: string;
  version: number;
  label: string;
  status: WorkOrderVisitStatus;
  windowStart: string;
  windowEnd: string | null;
  crew: readonly WorkOrderVisitPerson[];
  /** Whether the visit carries the job's own booking, whose move reads the tier warning and asks why. */
  carriesBooking: boolean;
  /** 🔒 Decision 3100: why the visit cannot move; null or absent reads Can move. */
  fixedReason?: string | null;
  /** 🔒 Decision 5571: the lines it covers and each worker's; absent from an older reply. */
  lineIds?: string[];
  workerLineIds?: Record<string, string[]>;
}

/** 🔒 Edit Visit's draft: the visit's own window as wall clocks in the zone, its crew and its lead (decision 2813). */
export function bookingOfVisit(visit: VisitToEdit, zone: string): Booking {
  return {
    startsAt: wallClockInZone(visit.windowStart, zone),
    endsAt: visit.windowEnd ? wallClockInZone(visit.windowEnd, zone) : '',
    workers: visit.crew.map((person) => ({ id: person.employmentId, label: person.name })),
    lead: visit.crew.find((person) => person.isLead)?.employmentId ?? '',
    fixed: visitIsFixed(visit),
    fixedReason: visit.fixedReason ?? '',
    ...(visit.lineIds ? { lineIds: [...visit.lineIds] } : {}),
    ...(visit.workerLineIds ? { workerLines: { ...visit.workerLineIds } } : {}),
  };
}

/** 🔒 Decision 3100: what a booking window sends for the visit's state, Can move or Fixed with the reason typed. */
export function fixedOf(booking: Pick<Booking, 'fixed' | 'fixedReason'>): { fixed: boolean; fixedReason: string | null } {
  return booking.fixed === true ? { fixed: true, fixedReason: booking.fixedReason?.trim() ?? '' } : { fixed: false, fixedReason: null };
}

/** 🔒 Decision 3100: Fixed waits for its reason before Save stands. */
export function fixedWaits(booking: Pick<Booking, 'fixed' | 'fixedReason'>): boolean {
  return booking.fixed === true && !booking.fixedReason?.trim();
}

/**
 * 🔒 Can move or Fixed (decision 3100; T2186): two choices, Can Move the
 * default, and Fixed opening the box for why it cannot move. Book the Visit,
 * Book Another Visit, Edit Visit and the job's Reschedule each draw it.
 */
export function VisitMovableFields({ booking, onChange, name }: { booking: Pick<Booking, 'fixed' | 'fixedReason'>; onChange: (next: { fixed: boolean; fixedReason: string }) => void; name: string }) {
  const fixed = booking.fixed === true;
  const reason = booking.fixedReason ?? '';
  return (
    <div data-visit-movable={fixed ? 'Fixed' : 'CanMove'}>
      <RadioGroup
        name={name}
        legend={t('workOrder.visits.movable')}
        orientation="horizontal"
        value={fixed ? 'Fixed' : 'CanMove'}
        options={[{ value: 'CanMove', label: t('workOrder.visits.canMove') }, { value: 'Fixed', label: t('workOrder.visits.fixed') }]}
        onChange={(value) => onChange({ fixed: value === 'Fixed', fixedReason: reason })}
      />
      {fixed ? (
        <>
          <TextInput size={Size.Large} label={t('workOrder.visits.fixedReason')} value={reason} onChange={(value) => onChange({ fixed, fixedReason: value })} name={`${name}-reason`} required />
          <p className={styles.hint}>{t('workOrder.visits.fixedHint')}</p>
        </>
      ) : null}
    </div>
  );
}

/**
 * 🔒 The Visits tile (decision 1908; T1157, T1201): each visit on the job with
 * its number, date and window, lead, crew, state and outcome, and Book Another
 * Visit. The rows sit in the List Viewer (piece 4), and Cancel Visit stands on
 * its action bar for one ticked visit still to come. A row opens the visit's
 * own page (T1263). A visit's state moves on the technician's taps on My Jobs,
 * so the tile says so rather than offering a desk button for them.
 */
export function WorkOrderVisits({ workOrderId, companyId, siteId = null, apiBaseUrl = '', onChanged }: { workOrderId: string; companyId: string; siteId?: string | null; apiBaseUrl?: string; onChanged: () => void }) {
  const read = useApi<WorkOrderVisitsAnswer>(workOrderVisitsPath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<number | null>(null);
  /** 🔒 Cancel Visit holds its refusal inside the window, which stays open with the reason typed (decision 3740). */
  const [cancelling, setCancelling] = useState<{ visit: WorkOrderVisitRow; reason: string; refusal: string | null } | null>(null);
  // 🔒 Decision 2813: the visit Edit Visit is open on, keyed by the visit and its version so a second opening reads the visit afresh.
  const [editing, setEditing] = useState<WorkOrderVisitRow | null>(null);
  const [sort, setSort] = useState<SortState | null>(ROUTE_ORDER);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [params, setParams] = useSearchParams();
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  const canBookFirst = answer?.canBookFirst === true;
  // 🔒 Decision 5570: the footer's Schedule on a Draft job opens the tile's inline form through `act=schedule`, not a window.
  const linked = params.get('act') === 'schedule' && answer !== null && (canBookFirst || answer.canBook);
  const formKey = booking ?? (linked ? 'linked' : null);
  const endLink = () => {
    if (params.get('act') !== 'schedule') return;
    const next = new URLSearchParams(params);
    next.delete('act');
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if (linked) globalThis.document?.querySelector(`[data-work-order-visits="${workOrderId}"]`)?.scrollIntoView?.({ block: 'start' });
  }, [linked, workOrderId]);
  if (!answer) return null;
  const visits = Array.isArray(answer.visits) ? answer.visits : [];
  const ticked = picked.length === 1 ? visits.find((row) => row.id === picked[0]) : undefined;
  const chosen = ticked && cancellable(ticked) ? ticked : null;
  const canEdit = answer.canEdit === true;
  // 🔒 Decision 12970: a ticked visit, open or not, offers Delete Anyway to the `admin.operations` holder alone.
  const deletesAnyway = answer.canDeleteAnyway === true;
  const anywayOn = ticked && deletesAnyway ? ticked : null;

  const when = (row: WorkOrderVisitRow) => {
    const start = formatDateTime(row.windowStart, zone, dateFormat);
    const end = row.windowEnd ? formatDateTime(row.windowEnd, zone, dateFormat) : null;
    return line(start, end ? `– ${end}` : null, zoneAbbreviation(row.windowStart, zone));
  };
  const cancel = async () => {
    if (!cancelling) return;
    const outcome = await post(workOrderVisitsPath(workOrderId, cancelling.visit.id, 'cancel'), { reason: cancelling.reason, version: cancelling.visit.version }, apiBaseUrl);
    // A refused cancel draws its message inside the window and leaves it open, as Book the Visit does (T1294; decision 3063).
    if (!outcome.ok) {
      setCancelling((was) => (was ? { ...was, refusal: outcome.message } : was));
      return;
    }
    setCancelling(null);
    read.reload?.();
    onChanged();
    // 🔒 Decision 3072: a cancelled last visit puts the job back among the unbooked, so the balloons read again at once.
    refreshMenuCounts();
  };

  return (
    <section data-work-order-visits={workOrderId}>
      {visits.length === 0 ? <p className={styles.hint} data-visits-none="true">{t('workOrder.visits.none')}</p> : (
        <>
          {deletesAnyway || ((answer.canCancel || canEdit) && visits.some(cancellable)) ? <p className={styles.hint} data-visits-cancel-hint="true">{t(deletesAnyway ? 'workOrder.visits.tickToDeleteAnyway' : 'workOrder.visits.tickToCancel')}</p> : null}
          <ListViewer
            viewStore={{ scopeKey: viewerScopeKey('work-order-visits'), apiBaseUrl, param: 'visitsView' }}
            recordType={t('workOrder.visits.recordType')}
            views={[{ id: 'all', name: t('workOrder.visits.view') }]}
            currentViewId="all"
            onViewChange={() => undefined}
            columns={COLUMNS}
            rows={visits.map((row, position) => visitCells(row, position, when))}
            idOf={(cell) => cell.id}
            read={(cell, key) => cell[key as keyof VisitCells]}
            cellMarker={(cell, key) => {
              const row = key === 'crew' ? visits.find((visit) => visit.id === cell.id) : undefined;
              return row ? notOpenedChips(row, zone, dateFormat) : null;
            }}
            // 🔒 T3859: a row with a Not Yet Opened chip lets its Crew cell wrap, so the chips stand beneath the names and are never cut off by the cell's ellipsis.
            rowStyle={(cell) => { const row = visits.find((visit) => visit.id === cell.id); return row && crewNotYetOpened(row).length > 0 ? { name: 'crew-not-opened', className: styles.visitCrewWaiting ?? '' } : null; }}
            sort={sort}
            onSortChange={setSort}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            emptyAction={t('workOrder.visits.none')}
            selectable={answer.canCancel || canEdit || deletesAnyway}
            onNavigate={(cell) => navigate(visitPageRoute(workOrderId, cell.id))}
            onSelectionChange={setPicked}
            selectionActions={chosen || anywayOn ? (
              <span className={styles.chips} data-visits-actions={(chosen ?? anywayOn)!.id}>
                {/* 🔒 Decision 2813: Edit Visit beside Cancel Visit, for the one open visit ticked, as the List Viewer offers a row's action. */}
                {chosen && canEdit ? <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.visits.edit')]} actions={[() => setEditing(chosen)]} /> : null}
                {chosen && answer.canCancel ? <Button size={Size.Small} color={Color.Inactive} text={[t('workOrder.visits.cancel')]} actions={[() => setCancelling({ visit: chosen, reason: '', refusal: null })]} /> : null}
                {anywayOn ? (
                  <VisitDeleteAnyway
                    key={anywayOn.id}
                    workOrderId={workOrderId}
                    visitId={anywayOn.id}
                    label={anywayOn.label}
                    apiBaseUrl={apiBaseUrl}
                    onDeleted={() => {
                      setPicked([]);
                      read.reload?.();
                      onChanged();
                      refreshMenuCounts();
                    }}
                  />
                ) : null}
              </span>
            ) : <span className={styles.hint}>{t('workOrder.visits.chooseOne')}</span>}
            bandless
          />
        </>
      )}
      {/* 🔒 Decision 4340: a visit still to come with nobody on it says so, since booking its crew is what sends it. */}
      {visits.filter((row) => row.status === 'Scheduled' && row.crew.length === 0).map((row) => <p key={row.id} className={styles.hint} data-visits-nobody-booked={row.label}>{fill(t('workOrder.visits.nobodyBooked'), { visit: row.label })}</p>)}
      {visits.length > 0 ? <p className={styles.hint} data-visits-taps-hint="true">{t('workOrder.visits.tapsHint')}</p> : null}
      {/* 🔒 Decisions 0151 and 9140 (T2367): the Load-Out Check is asked while a visit is Dispatched and none has started, keyed to the visits so a new booking reads it afresh. */}
      {visits.some((row) => loadOutFires(row.status)) && !visits.some((row) => row.status === 'InProgress') ? <WorkOrderLoadOut key={visits.map((row) => `${row.id}:${row.version}`).join(',')} workOrderId={workOrderId} apiBaseUrl={apiBaseUrl} /> : null}
      {(answer.canBook || canBookFirst) && formKey === null ? <Button size={Size.Small} color={Color.Navy} text={[t(canBookFirst ? 'workOrder.booking.bookFirst' : 'workOrder.visits.bookAnother')]} actions={[() => setBooking(Date.now())]} /> : null}
      {/* 🔒 Decision 2111 (T889): the job's checklist, where an item left unanswered is signed off. */}
      <span data-visits-checklist={workOrderId}><Button size={Size.Small} color={Color.Inactive} text={[t('checklist.page.open')]} actions={[() => navigate(jobChecklistRoute(workOrderId))]} /></span>

      {formKey !== null ? (
        <InlineBookVisit
          key={formKey}
          workOrderId={workOrderId}
          companyId={companyId}
          siteId={siteId}
          apiBaseUrl={apiBaseUrl}
          first={canBookFirst}
          defaultMinutes={answer.jobTypeMinutes ?? null}
          lines={Array.isArray(answer.lines) ? answer.lines : []}
          onClose={() => {
            setBooking(null);
            endLink();
          }}
          onBooked={() => {
            setBooking(null);
            endLink();
            read.reload?.();
            onChanged();
          }}
        />
      ) : null}

      {editing !== null ? (
        <EditVisitModal
          key={`${editing.id}:${editing.version}`}
          workOrderId={workOrderId}
          companyId={companyId}
          apiBaseUrl={apiBaseUrl}
          visit={{ ...editing, carriesBooking: editing.carriesBooking === true }}
          lines={Array.isArray(answer.lines) ? answer.lines : []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            read.reload?.();
            onChanged();
          }}
        />
      ) : null}

      {/* 🔒 Decisions 3060 and 3740: the reason is a form, so Escape, the × and Cancel ask before a typed reason is dropped, and a refusal reads inside. */}
      <FormFrame
        host="window"
        open={cancelling !== null}
        title={t('workOrder.visits.cancelTitle')}
        {...(cancelling ? { subtitle: cancelling.visit.label } : {})}
        onCancel={() => setCancelling(null)}
        dataForm="work-order-cancel-visit"
        typed={(cancelling?.reason ?? '') !== ''}
        act={{ label: t('workOrder.visits.cancel'), disabled: !cancelling?.reason.trim(), onAct: () => cancel() }}
        refusal={cancelling?.refusal ?? null}
        onDismissRefusal={() => setCancelling((was) => (was ? { ...was, refusal: null } : was))}
      >
        <TextInput size={Size.Large} label={t('workOrder.visits.cancelReason')} value={cancelling?.reason ?? ''} onChange={(reason) => setCancelling((was) => (was ? { ...was, reason } : was))} name="visit-cancel-reason" required />
      </FormFrame>
    </section>
  );
}

/**
 * 🔒 Raise Work Order's own use of the window (decision 2814; T2049): the
 * route the save posts to, which writes the job and books this visit in one
 * write, and the request's need read above the fields. No job exists yet, so
 * an empty Starts is a choice too: it raises the job with no visit.
 */
export interface RaiseTarget {
  path: string;
  need: string | null;
  /** 🔒 Decision 5570: the request's site, where the raise's Who Can Go ranks from; null draws none. */
  siteId?: string | null;
}

/** The rate change a move of the job's own booking makes, read before the new start is confirmed (piece 29). */
interface TierWarning {
  startsAt: string;
  toTierId: string;
  fromTierName: string;
  toTierName: string;
  fromModifier: number;
  toModifier: number;
}

/**
 * 🔒 Book Another Visit's window (decision 1901): the start, the end, the
 * workers going and their lead. The Visits tile opens it blank, and the visit's
 * page opens it as Book a Return Visit with that visit's crew and lead (T1263).
 * 🔒 Opened on a visit it is Edit Visit (decision 2813): the same fields on the
 * visit's own window, crew and lead, saved in one write at the version read. A
 * started visit's window reads as read-only and says so. A visit carrying the
 * job's own booking asks why it moves and shows the tier warning before a new
 * start is confirmed, as the job's Reschedule always has.
 * 🔒 Opened by Raise Work Order (decision 2814) it posts to the request's
 * raise, prefilled with the request's preferred window, and the answer names
 * the job it wrote.
 * 🔨 Keyed by its caller, so a second opening never shows the first draft.
 */
export function BookVisitModal({ workOrderId, companyId, apiBaseUrl = '', initial, draft, initialReason = '', title = t('workOrder.visits.bookTitle'), editing = null, raise = null, timeZone, defaultMinutes = null, lines = [], onClose, onBooked }: {
  workOrderId: string; companyId: string; apiBaseUrl?: string; initial: Booking;
  /** 🔒 Decision 5571: the job's labor lines the window ticks; empty draws none and sends none. */
  lines?: readonly VisitLineOption[];
  /** 🔒 Piece 29 (T1710): the job type's estimated minutes, the length a window opened with no end takes from its start. */
  defaultMinutes?: number | null;
  /** 🔒 Decision 3101: a window opened on a proposed time, read against `initial` so the move still asks why and reads the tier. */
  draft?: Booking; initialReason?: string;
  title?: string; editing?: VisitToEdit | null; raise?: RaiseTarget | null; timeZone?: string; onClose: () => void; onBooked: (answer?: Record<string, string> | null) => void;
}) {
  const viewerZone = useViewerZone();
  const zone = timeZone ?? viewerZone;
  const [booking, setBooking] = useState<Booking>(draft ?? initial);
  const [reason, setReason] = useState(initialReason);
  const [warning, setWarning] = useState<TierWarning | null>(null);
  /** 🔒 A refused booking reads inside the window, which stays open with what was typed (decision 3063). */
  const [refusal, setRefusal] = useState<string | null>(null);
  const windowFixed = editing !== null && !visitWindowEditable(editing.status);
  /**
   * 🔒 What the leave guard reads (decision 3061): any answer a person gave since the window opened. What the opener laid in, a proposed time or a
   * return visit's crew, is not typed, so the booking is read against the draft the window mounted with.
   */
  const laidIn = draft ?? initial;
  const typed = booking.startsAt !== laidIn.startsAt || booking.endsAt !== laidIn.endsAt || booking.lead !== laidIn.lead
    || booking.workers.map((chip) => chip.id).join() !== laidIn.workers.map((chip) => chip.id).join()
    || (booking.fixed === true) !== (laidIn.fixed === true) || (booking.fixedReason ?? '') !== (laidIn.fixedReason ?? '') || reason !== initialReason
    || JSON.stringify(booking.lineIds ?? null) !== JSON.stringify(laidIn.lineIds ?? null) || JSON.stringify(booking.workerLines ?? null) !== JSON.stringify(laidIn.workerLines ?? null);
  // 🔒 Decision 2813: a new start on the visit carrying the job's own booking moves that booking, which records why.
  const bookingMoves = editing !== null && editing.carriesBooking && booking.startsAt !== initial.startsAt;
  const warned = warning !== null && warning.startsAt === booking.startsAt;
  // 🔒 Decision 2814: the raise may clear both dates and write the job unbooked, but a crew or an end needs a start.
  const raiseWaits = raise !== null && !booking.startsAt && (booking.workers.length > 0 || Boolean(booking.endsAt));
  /**
   * 🔒 Piece 29 (T1710): a window opened with no length ends the type's estimated minutes after its start, and keeps
   * following the start until the end is typed. An end the opener laid in, or one a person typed, is never moved.
   */
  const endFollows = defaultMinutes !== null && defaultMinutes > 0 && laidIn.endsAt === '';
  const defaultEnd = (start: string) => (endFollows ? wallClockAfterMinutes(start, defaultMinutes, zone) ?? '' : '');
  const startAt = (startsAt: string) => {
    const following = endFollows && (booking.endsAt === '' || booking.endsAt === defaultEnd(booking.startsAt));
    setBooking({ ...booking, startsAt, endsAt: following ? defaultEnd(startsAt) : booking.endsAt });
  };
  /** 🔨 T2064: one save at a time, so a second press while the first is in flight sends nothing and Save reads as waiting. */
  const inFlight = useRef(false);
  const [saving, setSaving] = useState(false);
  const book = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await bookNow();
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  const bookNow = async () => {
    setRefusal(null);
    const people = { workerEmploymentIds: booking.workers.map((chip) => chip.id), leadEmploymentId: booking.lead || null };
    if (raise) {
      const raised = await post<Record<string, string>>(raise.path, { startsAt: booking.startsAt || null, endsAt: booking.endsAt || null, timeZone: zone, ...people, ...fixedOf(booking) }, apiBaseUrl);
      if (!raised.ok) setRefusal(raised.message);
      else {
        refreshMenuCounts();
        onBooked(raised.data);
      }
      return;
    }
    if (!editing) {
      const outcome = await post(workOrderVisitsPath(workOrderId), { startsAt: booking.startsAt, endsAt: booking.endsAt || null, timeZone: zone, ...people, ...fixedOf(booking), ...bookingLines(booking, lines) }, apiBaseUrl);
      // A refused booking draws its message and leaves the window open, as the Labour tile does (T1294).
      if (!outcome.ok) setRefusal(outcome.message);
      else {
        // 🔒 Decision 3072: a booked visit leaves the Dispatch Board's balloon at once, wherever the window was opened.
        refreshMenuCounts();
        onBooked();
      }
      return;
    }
    // 🔨 Piece 29: a move of the job's own booking reads the re-derived tier first; a change of tier shows the warning and waits for a second press.
    if (bookingMoves && !warned) {
      const answered = await get<Omit<TierWarning, 'startsAt'> & { changes: boolean }>(`${WORK_ORDERS_PATH}/${workOrderId}/reschedule-preview?startsAt=${encodeURIComponent(booking.startsAt)}&timeZone=${encodeURIComponent(zone)}`, apiBaseUrl);
      if (!answered.ok) return setRefusal(answered.message);
      if (answered.data?.changes) return setWarning({ startsAt: booking.startsAt, toTierId: answered.data.toTierId, fromTierName: answered.data.fromTierName, toTierName: answered.data.toTierName, fromModifier: answered.data.fromModifier, toModifier: answered.data.toModifier });
    }
    const input: EditVisitInput = { startsAt: booking.startsAt, endsAt: booking.endsAt || null, timeZone: zone, ...people, ...fixedOf(booking), ...bookingLines(booking, lines, true), version: editing.version, reason: reason.trim() || null, acknowledgedTierId: warned ? warning.toTierId : null };
    const outcome = await post(workOrderVisitsPath(workOrderId, editing.id, 'edit'), input, apiBaseUrl);
    if (!outcome.ok) setRefusal(outcome.message);
    else {
      // A changed crew moves somebody's My Jobs count.
      refreshMenuCounts();
      onBooked();
    }
  };
  return (
    <>
      <FormFrame
        host="window"
        open
        title={title}
        {...(editing ? { subtitle: editing.label } : {})}
        onCancel={onClose}
        dataForm={raise ? 'deal-raise-work-order' : editing ? 'work-order-edit-visit' : 'work-order-book-another-visit'}
        typed={typed}
        act={{ label: warned ? t('workOrder.reschedule.confirm') : t('settings.save'), disabled: saving || fixedWaits(booking) || (raise ? raiseWaits : !booking.startsAt || (bookingMoves && !reason.trim())), onAct: () => book() }}
        refusal={refusal}
        onDismissRefusal={() => setRefusal(null)}
      >
        {raise ? <p className={styles.hint} data-raise-need="true">{line(t('deal.request.raiseNeed'), raise.need ?? t('deal.request.raiseNoNeed'))}</p> : null}
        <p className={styles.hint}>{t(raise ? 'deal.request.raiseWindowHint' : editing ? 'workOrder.visits.editHint' : 'workOrder.visits.bookHint')}</p>
        {windowFixed ? <p className={styles.hint} data-visit-window-fixed="true">{t('workOrder.visits.startedWindow')}</p> : null}
        {/* 🔒 Decision 5570: the one set of visit fields; the raise ranks Who Can Go for the request's site and carries Can this visit move?. */}
        <VisitBookingFields booking={booking} onChange={setBooking} onStart={startAt} zone={zone} apiBaseUrl={apiBaseUrl} companyId={companyId} name="visit" whoCanGo={raise?.siteId ? { siteId: raise.siteId, workOrderId: null } : null} startRequired={!raise} windowFixed={windowFixed} lines={lines} />
        {editing?.carriesBooking ? (
          <TextInput size={Size.Large} label={t('workOrder.visits.moveReason')} value={reason} onChange={setReason} name="visit-move-reason" required={bookingMoves} />
        ) : null}
        {/* 🔒 Piece 29: the rate change a move makes, read before the new start is confirmed. */}
        {warned ? (
          <p className={styles.problem} data-reschedule-warning={warning.toTierName}>{line(t('workOrder.reschedule.warning'), warning.fromTierName, `×${warning.fromModifier}`, '→', warning.toTierName, `×${warning.toModifier}`)}</p>
        ) : null}
      </FormFrame>
    </>
  );
}

/**
 * 🔒 Edit Visit (decision 2813; T2048): the booking window opened on the
 * visit's own window, crew and lead, titled Edit Visit. The tile, the visit's
 * page and the job's Reschedule open this one window.
 */
export function EditVisitModal({ workOrderId, companyId, apiBaseUrl = '', visit, draft, initialReason, title = t('workOrder.visits.editTitle'), timeZone, lines = [], onClose, onSaved }: {
  workOrderId: string; companyId: string; apiBaseUrl?: string; visit: VisitToEdit;
  /** 🔒 Decision 5571: the job's labor lines Edit Visit ticks; empty draws none and keeps the visit's. */
  lines?: readonly VisitLineOption[];
  /** 🔒 Decision 3101: Propose a New Time opens the Reschedule on the proposed window and crew, with the reason filled. */
  draft?: Booking; initialReason?: string;
  title?: string; timeZone?: string; onClose: () => void; onSaved: () => void;
}) {
  const viewerZone = useViewerZone();
  const zone = timeZone ?? viewerZone;
  const initial = useMemo(() => bookingOfVisit(visit, zone), [visit, zone]);
  return (
    <BookVisitModal workOrderId={workOrderId} companyId={companyId} apiBaseUrl={apiBaseUrl} initial={initial} {...(draft ? { draft } : {})} {...(initialReason ? { initialReason } : {})} title={title} editing={visit} timeZone={zone} lines={lines} onClose={onClose} onBooked={onSaved} />
  );
}

/** 🔒 Decision 5570: Who Can Go inside a booking form, the board's ranking for the site and the window; relative to the API's own root. */
export const whoCanGoPath = (siteId: string, startsAt: string, endsAt: string, timeZone: string, workOrderId: string | null): string =>
  `/work/who-can-go?siteId=${encodeURIComponent(siteId)}&startsAt=${encodeURIComponent(startsAt)}${endsAt ? `&endsAt=${encodeURIComponent(endsAt)}` : ''}&timeZone=${encodeURIComponent(timeZone)}${workOrderId ? `&workOrderId=${encodeURIComponent(workOrderId)}` : ''}`;

/** Where Who Can Go reads its ranking from: the job's site, and the job itself once it exists. */
export interface WhoCanGoTarget {
  siteId: string;
  workOrderId: string | null;
}

/**
 * 🔒 Who Can Go inline (decision 5570, the owner's W5a): the Dispatch Board's
 * own ranking for the site and the window the form names, free in the window
 * first, then qualified, then nearest. Add puts the worker on the crew chips.
 * A worker the site's gate blocks cannot be added, and the reason stands under
 * the name; a level below the line's is named and added, as Book the visit
 * always allowed.
 */
export function WhoCanGoRows({ target, booking, zone, apiBaseUrl = '', onAdd }: { target: WhoCanGoTarget; booking: Pick<Booking, 'startsAt' | 'endsAt' | 'workers'>; zone: string; apiBaseUrl?: string; onAdd: (candidate: BoardCandidate) => void }) {
  const path = target.siteId && booking.startsAt ? whoCanGoPath(target.siteId, booking.startsAt, booking.endsAt, zone, target.workOrderId) : null;
  const read = useApi<WhoCanGoAnswer>(path, apiBaseUrl);
  // 🔨 A reply missing its rows draws nothing rather than crashing the form (CLAUDE.md, a fixture carries the server's shape).
  const candidates = read.state.kind === 'ready' && Array.isArray(read.state.data?.candidates) ? read.state.data.candidates : null;
  const chosen = new Set(booking.workers.map((chip) => chip.id));
  return (
    <section className={bookingStyles.whoCanGo} data-who-can-go={target.workOrderId ?? 'new'}>
      <h3 className={bookingStyles.heading}>{t('work.dispatch.candidates')}</h3>
      <p className={styles.hint}>{path ? t('workOrder.booking.whoCanGoHint') : t('workOrder.booking.whoCanGoWaits')}</p>
      {read.state.kind === 'failed' ? <p className={bookingStyles.problem}>{read.state.error}</p> : null}
      {candidates && candidates.length === 0 ? <p className={styles.hint}>{t('work.dispatch.nobody')}</p> : null}
      {candidates && candidates.length > 0 ? (
        <ul className={bookingStyles.candidates}>
          {candidates.map((candidate) => {
            const gated = candidate.blocked.length > 0 && !candidate.passable && candidate.skillLevelName !== null;
            const added = chosen.has(candidate.employmentId);
            return (
              <li key={candidate.employmentId} className={bookingStyles.candidate} data-who-can-go-row={candidate.name} data-who-can-go-free={candidate.clash ? 'false' : 'true'}>
                <span className={bookingStyles.candidateName}>{candidate.name}</span>
                <span className={bookingStyles.muted}>{candidate.skillLevelName ?? t('work.dispatch.noLevel')}</span>
                <span className={bookingStyles.muted}>{candidate.driveMinutes === null ? t('work.dispatch.noDrive') : line(String(candidate.driveMinutes), t('work.dispatch.minutes'))}</span>
                {candidate.clash ? <span className={bookingStyles.busy}>{fill(t('work.dispatch.busy'), { event: candidate.clash?.subject ?? '' })}</span> : <span className={bookingStyles.muted}>{t('workOrder.booking.free')}</span>}
                {candidate.blocked.map((reason) => <span key={reason} className={bookingStyles.problem}>{reason}</span>)}
                {/* 🔒 Decision 5572: a candidate with no login is named before they are added. */}
                {candidate.hasLogin === false ? <span className={bookingStyles.busy} data-who-can-go-no-login={candidate.name}>{fill(t('workOrder.booking.noLogin'), { name: candidate.name })}</span> : null}
                <span data-who-can-go-add={candidate.name}>
                  <Button size={Size.Small} color={added ? Color.Inactive : Color.Navy} text={[t(added ? 'workOrder.booking.added' : 'workOrder.booking.add')]} disabled={added || gated} actions={[() => onAdd(candidate)]} />
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * 🔒 The safeguard of decision 5572 (T2785): beside each chosen worker with no
 * login, or with no open employment among the workers the form lists, the
 * sentence that the job will not reach their My Jobs. The booking is not
 * refused; the booker is told before Save.
 */
export function workerReachText(name: string, known: { hasLogin?: boolean } | undefined): string | null {
  if (!known) return fill(t('workOrder.booking.noEmployment'), { name });
  return known.hasLogin === false ? fill(t('workOrder.booking.noLogin'), { name }) : null;
}

function WorkerReach({ workers, known }: { workers: readonly Chip[]; known: readonly { employmentId: string; hasLogin?: boolean }[] }) {
  const said = workers.flatMap((chip) => {
    const text = workerReachText(chip.label, known.find((worker) => worker.employmentId === chip.id));
    return text ? [{ id: chip.id, label: chip.label, text }] : [];
  });
  if (said.length === 0) return null;
  return (
    <div className={bookingStyles.workerLines}>
      {said.map((row) => <span key={row.id} className={bookingStyles.busy} data-visit-worker-reach={row.label}>{row.text}</span>)}
    </div>
  );
}

/** 🔒 Decision 5571: the lines a booking covers, every line where none were unticked. */
export function coveredLines(booking: Pick<Booking, 'lineIds'>, lines: readonly VisitLineOption[]): string[] {
  const all = lines.map((line) => line.lineId);
  return booking.lineIds ? all.filter((id) => booking.lineIds!.includes(id)) : all;
}

/** 🔒 Decision 5571: what a booking sends for its lines, nothing where every line goes with every worker. */
export function bookingLines(booking: Pick<Booking, 'lineIds' | 'workerLines' | 'workers'>, lines: readonly VisitLineOption[], always = false): VisitLinesInput {
  if (lines.length === 0) return {};
  const all = lines.map((line) => line.lineId);
  const crew = booking.workers.map((chip) => chip.id);
  // 🔨 Edit Visit always names them, so widening a narrowed visit back to every line is not read as keep what it holds.
  if (!always) return visitLinesInput(all, booking.lineIds ? coveredLines(booking, lines) : null, booking.workerLines ?? null, crew);
  const covered = coveredLines(booking, lines);
  return { visitLineIds: covered, workerLineIds: Object.fromEntries(crew.map((id) => [id, (booking.workerLines?.[id] ?? covered).filter((lineId) => covered.includes(lineId))])) };
}

/**
 * 🔒 The lines of a visit and of each worker (decision 5571, the owner's note
 * on W5a): every line of the job ticked by default, and under it each worker
 * going with the covered lines they are sent for, every one by default.
 */
export function VisitLineFields({ booking, onChange, lines, name }: { booking: Booking; onChange: (next: Booking) => void; lines: readonly VisitLineOption[]; name: string }) {
  const covered = coveredLines(booking, lines);
  const toggleLine = (lineId: string, on: boolean) => {
    const next = lines.map((line) => line.lineId).filter((id) => (id === lineId ? on : covered.includes(id)));
    onChange({ ...booking, lineIds: next });
  };
  const workerHas = (employmentId: string) => (booking.workerLines?.[employmentId] ?? covered).filter((id) => covered.includes(id));
  const toggleWorker = (employmentId: string, lineId: string, on: boolean) => {
    const had = workerHas(employmentId);
    const next = covered.filter((id) => (id === lineId ? on : had.includes(id)));
    onChange({ ...booking, workerLines: { ...(booking.workerLines ?? {}), [employmentId]: next } });
  };
  return (
    <fieldset className={bookingStyles.lines} data-visit-lines={covered.length}>
      <legend className={bookingStyles.heading}>{t('workOrder.booking.lines')}</legend>
      <div className={bookingStyles.lineChoices}>
        {lines.map((line) => (
          <span key={line.lineId} data-visit-line-choice={line.label}>
            <Checkbox label={line.label} checked={covered.includes(line.lineId)} onChange={(on) => toggleLine(line.lineId, on)} name={`${name}-line-${line.lineId}`} />
          </span>
        ))}
      </div>
      {covered.length === 0 ? <p className={styles.hint} data-visit-lines-none="true">{t('workOrder.booking.noLines')}</p> : null}
      {covered.length > 0 && booking.workers.map((chip) => (
        <div key={chip.id} className={bookingStyles.workerLines} data-visit-worker-lines={chip.label}>
          <span className={bookingStyles.muted}>{fill(t('workOrder.booking.workerLines'), { name: chip.label })}</span>
          <div className={bookingStyles.lineChoices}>
            {lines.filter((line) => covered.includes(line.lineId)).map((line) => (
              <span key={line.lineId} data-visit-worker-line={`${chip.label}:${line.label}`}>
                <Checkbox size={Size.Small} label={line.label} checked={workerHas(chip.id).includes(line.lineId)} onChange={(on) => toggleWorker(chip.id, line.lineId, on)} name={`${name}-worker-${chip.id}-${line.lineId}`} />
              </span>
            ))}
          </div>
          {workerHas(chip.id).length === 0 ? <span className={bookingStyles.busy} data-visit-worker-no-line={chip.label}>{fill(t('workOrder.booking.workerNoLine'), { name: chip.label })}</span> : null}
        </div>
      ))}
    </fieldset>
  );
}

/**
 * 🔒 The visit's fields as every booking form draws them (decisions 0872,
 * 1901, 3100, 5570): Starts and Ends, Assigned workers with Who Can Go inline,
 * Lead worker, and Can this visit move?. + New Work Order, Raise Work Order,
 * Book Another Visit on the Visits tile, Edit Visit and Book a Return Visit all
 * draw this one set, so a booking reads the same wherever it is made.
 */
export function VisitBookingFields({ booking, onChange, zone, apiBaseUrl = '', companyId, name, whoCanGo = null, startRequired = true, windowFixed = false, movable = true, onStart, lines = [] }: {
  booking: Booking; onChange: (next: Booking) => void; zone: string; apiBaseUrl?: string; companyId: string | null; name: string;
  /** 🔒 Decision 5571: the job's labor lines the form ticks; empty draws no lines section. */
  lines?: readonly VisitLineOption[];
  /** Where Who Can Go reads from; null draws no Who Can Go, as Edit Visit's own window does not need one. */
  whoCanGo?: WhoCanGoTarget | null;
  startRequired?: boolean; windowFixed?: boolean; movable?: boolean;
  /** A start that also moves the end, the job type's length following it (T1710); absent sets the start alone. */
  onStart?: (startsAt: string) => void;
}) {
  const options = useWorkOrderOptions(companyId, apiBaseUrl);
  const addNew = useAddNew(apiBaseUrl);
  const workerSearch = useMemo(() => searchWithin(((options.state.kind === 'ready' ? options.state.data?.workers : null) ?? []).map((worker) => ({ id: worker.employmentId, line1: worker.name, line2: worker.skillLevelName ?? t('workOrder.line.unassignable') }))), [options.state]);
  const setWorkers = (workers: Chip[]) => onChange({ ...booking, workers, lead: workers.some((chip) => chip.id === booking.lead) ? booking.lead : workers[0]?.id ?? '' });
  const reach = options.state.kind === 'ready' ? options.state.data?.workers ?? null : null;
  return (
    <>
      <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.schedule.start')} value={booking.startsAt} onChange={(value) => (onStart ? onStart(value) : onChange({ ...booking, startsAt: value }))} name={`${name}-start`} required={startRequired} disabled={windowFixed} />
      <DatePicker size={Size.Medium} mode="datetime" label={t('workOrder.schedule.end')} value={booking.endsAt} onChange={(value) => onChange({ ...booking, endsAt: value })} name={`${name}-end`} disabled={windowFixed} {...(booking.startsAt ? { min: booking.startsAt } : {})} />
      <Chips label={t('workOrder.schedule.workers')} entityLabel={t('workOrder.schedule.workerEntity')} value={booking.workers} onChange={setWorkers} search={workerSearch} onAddNew={() => addNew.open({ kind: 'employee' }, () => undefined)} />
      {reach ? <WorkerReach workers={booking.workers} known={reach} /> : null}
      {whoCanGo ? <WhoCanGoRows target={whoCanGo} booking={booking} zone={zone} apiBaseUrl={apiBaseUrl} onAdd={(candidate) => setWorkers([...booking.workers, { id: candidate.employmentId, label: candidate.name }])} /> : null}
      {booking.workers.length > 0 ? (
        <Select size={Size.Medium} label={t('workOrder.schedule.lead')} value={booking.lead} onChange={(lead) => onChange({ ...booking, lead })} options={booking.workers.map((chip) => ({ value: chip.id, label: chip.label }))} hint={t('workOrder.schedule.leadHint')} dataAttribute={{ 'data-visit-lead-choice': 'true' }} required />
      ) : null}
      {lines.length > 0 ? <VisitLineFields booking={booking} onChange={onChange} lines={lines} name={name} /> : null}
      {/* 🔒 Decision 3100: Can move or Fixed, which every booking form now carries (decision 5570). */}
      {movable ? <VisitMovableFields booking={booking} onChange={(next) => onChange({ ...booking, ...next })} name={`${name}-movable`} /> : null}
      {addNew.host}
    </>
  );
}

/**
 * 🔒 Book Another Visit on the Visits tile, inline on the Work Order page with
 * no window (decision 5570, the owner's W5a). The same fields as every booking
 * form; a Draft job's first visit is booked here too, through the job's own
 * booking, so the footer's Schedule opens this form rather than a window.
 */
export function InlineBookVisit({ workOrderId, companyId, siteId, apiBaseUrl = '', first, defaultMinutes = null, lines = [], onClose, onBooked }: {
  workOrderId: string; companyId: string; siteId: string | null; apiBaseUrl?: string;
  /** 🔒 Decision 5571: the job's labor lines the form ticks. */
  lines?: readonly VisitLineOption[];
  /** Whether this books the job's first visit, through its own booking, rather than another. */
  first: boolean;
  defaultMinutes?: number | null; onClose: () => void; onBooked: () => void;
}) {
  const zone = useViewerZone();
  const [booking, setBooking] = useState<Booking>(EMPTY_BOOKING);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const endFollows = defaultMinutes !== null && defaultMinutes > 0;
  const defaultEnd = (start: string) => (endFollows ? wallClockAfterMinutes(start, defaultMinutes, zone) ?? '' : '');
  const startAt = (startsAt: string) => {
    const following = endFollows && (booking.endsAt === '' || booking.endsAt === defaultEnd(booking.startsAt));
    setBooking({ ...booking, startsAt, endsAt: following ? defaultEnd(startsAt) : booking.endsAt });
  };
  const save = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setRefusal(null);
    const body = { startsAt: booking.startsAt, endsAt: booking.endsAt || null, timeZone: zone, workerEmploymentIds: booking.workers.map((chip) => chip.id), leadEmploymentId: booking.lead || null, ...fixedOf(booking), ...bookingLines(booking, lines) };
    const outcome = await post(first ? `${WORK_ORDERS_PATH}/${workOrderId}/schedule` : workOrderVisitsPath(workOrderId), body, apiBaseUrl);
    inFlight.current = false;
    setSaving(false);
    // 🔒 Decision 3063: a refused booking reads inside the form, which stays open with what was typed.
    if (!outcome.ok) return setRefusal(outcome.message);
    refreshMenuCounts();
    onBooked();
  };
  return (
    <section className={bookingStyles.inline} data-form={first ? 'work-order-book-visit' : 'work-order-book-another-visit'}>
      <h3 className={bookingStyles.heading}>{t(first ? 'workOrder.schedule.title' : 'workOrder.visits.bookTitle')}</h3>
      <p className={styles.hint}>{t('workOrder.booking.inlineHint')}</p>
      <VisitBookingFields booking={booking} onChange={setBooking} onStart={startAt} zone={zone} apiBaseUrl={apiBaseUrl} companyId={companyId} name="inline-visit" whoCanGo={siteId ? { siteId, workOrderId } : null} lines={lines} />
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      <div className={bookingStyles.actions}>
        <Button size={Size.Medium} color={Color.Navy} text={[t('settings.save')]} disabled={saving || !booking.startsAt || fixedWaits(booking)} actions={[() => void save()]} />
        <Button size={Size.Medium} color={Color.Inactive} text={[t('modal.cancel')]} disabled={saving} actions={[onClose]} />
      </div>
    </section>
  );
}

/** 🔒 The tile's columns as they stood (decision 1908): the visit, its window, the lead, the crew, the state and the outcome. */
const COLUMNS: readonly ColumnDef[] = [
  { key: 'visit', label: t('workOrder.visits.visit'), role: 'primary', type: 'text', fit: true },
  { key: 'window', label: t('workOrder.visits.window'), role: 'secondary', type: 'text' },
  { key: 'lead', label: t('workOrder.visits.lead'), role: 'secondary', type: 'text' },
  { key: 'crew', label: t('workOrder.visits.crew'), role: 'secondary', type: 'text' },
  { key: 'state', label: t('workOrder.visits.state'), role: 'secondary', type: 'text' },
  { key: 'outcome', label: t('workOrder.visits.outcome'), role: 'secondary', type: 'text' },
  // 🔒 Decision 2910: the visit's hours, its notes and its Service Report's number and state.
  { key: 'hours', label: t('serviceReport.visits.hours'), role: 'secondary', type: 'text', fit: true },
  { key: 'notes', label: t('serviceReport.visits.notes'), role: 'secondary', type: 'number', fit: true },
  { key: 'report', label: t('serviceReport.visits.report'), role: 'secondary', type: 'text' },
];

/** 🔒 The route's order, the visit number, until a header is clicked. */
const ROUTE_ORDER: SortState = { key: 'position', direction: 'asc' };

/**
 * 🔒 Piece 29 (T3859; the owner's S9a): Not Yet Opened beside each crew member of a visit still to come who has not opened the job on
 * My Jobs, an inactive Pill whose hover says when that person last opened My Jobs, or that they never have. With more than one on the
 * crew each chip carries its person's name, so the chip says whose it is.
 */
function notOpenedChips(row: WorkOrderVisitRow, zone: string, dateFormat: DateFormat) {
  const waiting = crewNotYetOpened(row);
  if (waiting.length === 0) return null;
  return (
    <span className={styles.notOpened} data-crew-not-opened={row.label}>
      {waiting.map((person) => {
        const hover = person.myJobsOpenedAt ? fill(t('workOrder.visits.notOpenedSince'), { name: person.name, when: formatDateTime(person.myJobsOpenedAt, zone, dateFormat) }) : fill(t('workOrder.visits.notOpenedEver'), { name: person.name });
        // T3880: the hover is also the chip's label and the chip takes the keyboard's focus, so a keyboard or screen-reader user reads it too.
        return (
          <span key={person.employmentId} className={styles.chips} data-crew-not-opened-person={person.employmentId} title={hover} tabIndex={0} role="note" aria-label={[t('workOrder.visits.notOpened'), hover].join('. ')}>
            {row.crew.length > 1 ? <span className={styles.hint}>{person.name}</span> : null}
            <Pill text={t('workOrder.visits.notOpened')} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} />
          </span>
        );
      })}
    </span>
  );
}

/**
 * 🔒 Delete Anyway on the ticked visit (decision 12970, amending 2813): it reads
 * what the visit carries, then a Destructive button behind the typed phrase
 * whose dialog names what goes. Refused, it stays gray and says why; a refusal
 * from the delete itself reads beside it.
 */
function VisitDeleteAnyway({ workOrderId, visitId, label, apiBaseUrl, onDeleted }: { workOrderId: string; visitId: string; label: string; apiBaseUrl: string; onDeleted: () => void }) {
  const [deletion, setDeletion] = useState<VisitDeletion | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The read of what the visit carries failed, so its button says so instead of reading for ever (T3901). */
  const [unread, setUnread] = useState(false);
  useEffect(() => {
    let live = true;
    void get<VisitDeletion>(workOrderVisitsPath(workOrderId, visitId, 'deletion'), apiBaseUrl).then((answer) => {
      if (live) {
        if (answer.ok && answer.data) setDeletion(answer.data);
        else {
          setUnread(true);
          setRefusal(answer.ok ? null : answer.message);
        }
      }
    });
    return () => {
      live = false;
    };
  }, [workOrderId, visitId, apiBaseUrl]);
  const anyway = deletion?.anyway ?? null;
  const remove = async () => {
    setRefusal(null);
    const answer = await post<{ deleted: true }>(workOrderVisitsPath(workOrderId, visitId, 'delete-anyway'), {}, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onDeleted();
  };
  const reason = anyway?.refusal ?? (anyway ? null : t(unread ? 'workOrder.visits.deleteAnywayUnread' : 'workOrder.visits.deleteAnywayReading'));
  return (
    <span data-visit-delete-anyway={visitId}>
      <Button
        size={Size.Small}
        color={Color.Destructive}
        destructive={{ subject: anyway?.subject ?? deletion?.label ?? label, phrase: t('modal.deletePhrase'), toTrash: true }}
        text={[t('workOrder.visits.deleteAnyway')]}
        actions={[() => void remove()]}
        disabled={reason !== null}
        {...(reason ? { disabledReason: reason } : {})}
      />
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
    </span>
  );
}

/** A visit still to come, the one Cancel Visit takes. */
const cancellable = (row: WorkOrderVisitRow): boolean => row.status === 'Scheduled' || row.status === 'Dispatched' || row.status === 'InProgress';

/** One row as the List Viewer reads it. */
interface VisitCells {
  id: string;
  position: number;
  visit: string;
  window: string;
  lead: string;
  crew: string;
  state: string;
  outcome: string;
  hours: string;
  notes: string;
  report: string;
}

function visitCells(row: WorkOrderVisitRow, position: number, when: (row: WorkOrderVisitRow) => string): VisitCells {
  const state = WORK_ORDER_VISIT_STATUS_LABELS[row.status] ?? row.status;
  return {
    id: row.id,
    position,
    visit: row.label,
    window: when(row),
    // 🔒 The lead is marked beside their name (decision 1901).
    lead: row.lead ? line(row.lead.name, t('workOrder.visits.leadMark')) : t('workOrder.visits.noLead'),
    crew: row.crew.length === 0 ? t('workOrder.visits.nobody') : row.crew.map((person) => person.name).join(', '),
    state: row.status === 'Cancelled' && row.cancelReason ? line(state, t('workOrder.visits.cancelled'), row.cancelReason) : state,
    outcome: row.outcome === 'CouldNotComplete' ? t('workOrder.visits.couldNotComplete') : row.outcome === 'Completed' ? t('workOrder.visits.completed') : '',
    hours: row.hours === undefined ? '' : hoursLabel(row.hours),
    notes: row.noteCount === undefined ? '' : String(row.noteCount),
    report: row.reportNumber ? line(row.reportNumber, serviceReportStateLabel(row.reportState ?? null)) : '',
  };
}
